import { useState } from 'react';

export default function SubjectLineGenerator({ onGenerate }) {
  const [formData, setFormData] = useState({
    productName: "",
    description: "",
    tone: "Professional",
    campaignType: "Acquisition"
  });
  const [loading, setLoading] = useState(false);

    // Calculate live stats
  const charCount = formData.description.length;
  // Rule of thumb: 1 token is approx 4 characters
  const estTokens = Math.ceil(charCount / 4) + 50; 



  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim() || !formData.productName.trim()) return;
    
    setLoading(true);
    // Passes the entire object {productName, description, tone, campaignType}
    await onGenerate(formData); 
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mt-10 mb-4 font-sans text-slate-900 flex flex-col lg:flex-row gap-8">
       {/* LEFT SIDE: THE FORM (Flex-1) */}
      <div className="flex-1">
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Product Name Input */}
        <div>
          <label className="block mb-2 font-medium">Product or Service Name</label>
          <input
            type="text"
            name="productName"
            className="w-full px-4 py-3 border-2 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-slate-600"
            placeholder="e.g. BrewBound Coffee"
            value={formData.productName}
            onChange={handleChange}
            required
          />
        </div>

        {/* Dropdowns Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Tone Dropdown */}
          <div>
            <label className="block mb-2 font-medium">Tone</label>
            <select 
              name="tone"
              className="w-full px-4 py-3 border-2 rounded bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-slate-600"
              value={formData.tone}
              onChange={handleChange}
            >
              {["Professional", "Friendly", "Playful", "Urgent", "Inspirational"].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Campaign Type Dropdown */}
          <div>
            <label className="block mb-2 font-medium">Campaign Type</label>
            <select 
              name="campaignType"
              className="w-full px-4 py-3 border-2 rounded bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-slate-600"
              value={formData.campaignType}
              onChange={handleChange}
            >
              <option value="Acquisition">Acquisition (new users)</option>
              <option value="Activation">Activation (first use)</option>
              <option value="Engagement">Engagement (keep them active)</option>
              <option value="Retention">Retention (prevent churn)</option>
              <option value="Win-back">Win-back (reactivate)</option>
              <option value="Upsell">Upsell (sell more)</option>
            </select>
          </div>
        </div>

        {/* Description Textarea */}
        <div>
          <label className="block mb-2 font-medium">Enter Product or Service Description</label>
          <textarea
            name="description"
            rows="4"
            className="w-full px-4 py-3 border-2 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none text-slate-600 placeholder:text-slate-400"
            placeholder="Describe the specific offer or update..."
            value={formData.description}
            onChange={handleChange}
            required
          />
        </div>

        {/* Submit Button (Kept w-80 per your current style) */}
        <button
          type="submit"
          disabled={loading || !formData.description || !formData.productName}
          className={`w-80 flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-lg transition-all
            ${loading 
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
              : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98] shadow-lg shadow-indigo-200'
            }`}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Analyzing...
            </>
          ) : (
            "Generate Subject Lines"
          )}
        </button>
      </form>
      </div>

      {/* RIGHT SIDE: LIVE STATISTICS (W-80) */}
      <div className="lg:w-80 space-y-4">
        <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-6 sticky top-10">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">
            Campaign Insights
          </h3>
          
          <div className="space-y-6">
            {/* Character Count Stat */}
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-slate-600">Description Length</span>
                <span className={`text-sm font-bold ${charCount > 200 ? 'text-orange-500' : 'text-indigo-600'}`}>
                  {charCount} / 500
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${Math.min((charCount / 500) * 100, 100)}%` }}
                ></div>
              </div>
              <p className="text-[10px] text-slate-400 mt-1 italic">
                *Longer descriptions provide better AI context.
              </p>
            </div>

            {/* Token Est Stat */}
            <div className="pt-4 border-t border-slate-200">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Est. Request Tokens</span>
                <span className="text-sm font-mono font-bold text-slate-800">{estTokens}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-sm text-slate-600">Est. Cost</span>
                <span className="text-sm font-mono text-green-600 font-bold">
                  ${(estTokens * 0.0000025).toFixed(5)}
                </span>
              </div>
            </div>

            {/* Mobile Preview Warning */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700 leading-relaxed">
                <strong>Pro Tip:</strong> Stick to <strong>45 characters</strong> to ensure 
    your subject line isn't cut off on <strong>mobile screens</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
